import { useState } from "react";
import moment from "moment-timezone";
import { v4 as uuidV4 } from "uuid";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Combobox } from "@/components/ui/combobox";
import { useToast } from "@/components/ui/use-toast";
import { CfrDocument, CreateTemplateInstanceValidation, useCreateCfrTemplateInstance, useListCfrTemplates, CfrOptionalFieldInfo, useGetCfrPreviewTemplateDocument } from "@/lib/api/cfr";
import { useListMinimalUsers } from "@/lib/api/users";
import { useListCustomers } from "@/lib/api/customer";
import { ResponseResult, ServerError } from "@/lib/responseResults";
import { CommonDestinationTimezone } from "@/lib/utils";

interface CreateInstanceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialData?: {
    templateId?: string;
    customerId?: string;
    suggestionId?: string;

    contractNumber?: string;
    contractQuantity?: string;
    contract_shipping_dates?: string;
    contractType?: string;

    documentTitle?: string;
  }
  onSuccess?: () => void;
}

interface InstanceFormData {
  template_id: string;
  customer_id: string;
  is_test_send: boolean;
  contract_number?: string;
  contract_type?: string;
  contract_quantity?: string;
  document_title?: string;
  contract_shipping_dates?: string;
  contract_date?: string;
}

export function CreateInstanceDialog({ open, onOpenChange, initialData, onSuccess }: CreateInstanceDialogProps) {
  const { toast } = useToast();
  const createCfrTemplateInstance = useCreateCfrTemplateInstance();
  const getCfrPreviewTemplateDocument = useGetCfrPreviewTemplateDocument();
  const templatesResult = useListCfrTemplates();
  const minimalCustomerListResult = useListCustomers({ fetchInBackground: true, minimal: true });
  const [formData, setFormData] = useState<InstanceFormData>({
    template_id: initialData?.templateId || "",
    customer_id: initialData?.customerId || "",
    is_test_send: false,
    contract_number: initialData?.contractNumber || "",
    contract_type: initialData?.contractType || "",
    contract_quantity: initialData?.contractQuantity || "",
    contract_shipping_dates: initialData?.contract_shipping_dates || "",
    contract_date: moment.tz(CommonDestinationTimezone).format("YYYY-MM-DD"),
    document_title: initialData?.documentTitle || "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [instancePreview, setInstancePreview] = useState({
    isLoading: false,
    blob: null as Blob | null,
    showOptions: false,
  });

  const templates = templatesResult?.data?.data?.data || [];
  const customers = minimalCustomerListResult?.data?.data || [];

  const templateOptions = templates.map((template: CfrDocument) => ({
    value: template?.id || "",
    label: [
      template?.DocumentTemplate?.title || [template.crop_year.toString(), template.commodity_type, "CFR Declaration"!].filter(Boolean).join(" "),
      template?.Destination?.long_name || "",
    ].filter(Boolean).join(" - ")
  }));

  const userOptions = customers.map((customer: { long_name: string; id: string }) => ({
    value: customer.id,
    label: customer.long_name
  }));

  const assembleTemplateInstanceData = () => {
    const cfr_document = templates.find((template: CfrDocument) => template.id === formData.template_id);

    if (!cfr_document) {
      throw new Error("Template not found");
    }

    const meta_fields: typeof CfrOptionalFieldInfo._type = {};
    if (formData.contract_number) meta_fields.contract_number = formData.contract_number;
    if (formData.contract_type) meta_fields.contract_type = formData.contract_type;
    if (formData.contract_quantity) meta_fields.contract_quantity = formData.contract_quantity;
    if (formData.contract_shipping_dates) meta_fields.contract_shipping_dates = formData.contract_shipping_dates;
    if (formData.contract_date) meta_fields.contract_date = formData.contract_date;

    const payload = {
      cfr_document_id: cfr_document.id,
      group_id: uuidV4(),
      template_id: cfr_document.DocumentTemplate.id,
      customer_id: formData.customer_id,
      is_test_send: formData.is_test_send ?? false,
      suggestion_id: initialData?.suggestionId ? initialData?.suggestionId : undefined,
      meta_fields: Object.keys(meta_fields).length > 0 ? meta_fields : undefined,
      document_title: formData?.document_title?.trim() ? formData.document_title.trim() : undefined,
    };

    const parseResult = CreateTemplateInstanceValidation.safeParse(payload);

    if (!parseResult.success) {
      console.error("Failed to assemble template instance data", parseResult.error)
      throw new Error("Failed to validate template instance data");
    }

    return parseResult.data;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (!formData.template_id || !formData.customer_id) {
      toast({
        title: "Error",
        description: "Template and customer are required",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const templateInstanceData = assembleTemplateInstanceData();
      const apiResult = await createCfrTemplateInstance(templateInstanceData);
      if (!apiResult.success) {
        throw apiResult;
      }

      toast({
        title: "Send Queued",
        description: formData.is_test_send ? "Test send has been queued" : "Send has been queued",
      });

      onSuccess?.();
      onOpenChange(false);

      setFormData({
        template_id: initialData?.templateId || "",
        customer_id: initialData?.customerId || "",
        is_test_send: false,
        contract_number: "",
        contract_type: "",
        contract_quantity: "",
        contract_shipping_dates: "",
        contract_date: moment().format("YYYY-MM-DD"),
      });
    } catch (error) {
      console.error("Failed to create template instance", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to create template instance";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePreview = async () => {
    if (instancePreview.isLoading) return;

    if (!formData.template_id || !formData.customer_id) {
      toast({
        title: "Error",
        description: "Template and customer are required for preview",
        variant: "destructive",
      });
      return;
    }

    setInstancePreview({
      isLoading: true,
      blob: null,
      showOptions: false,
    });

    try {
      const templateInstanceData = assembleTemplateInstanceData();
      const result = await getCfrPreviewTemplateDocument(templateInstanceData);

      if (result instanceof ResponseResult && !result.success) {
        toast({
          title: "Error",
          description: result.message || "Failed to generate preview",
          variant: "destructive",
        });
        return;
      } else if (result instanceof ResponseResult) {
         toast({
          title: "Error",
          description: "Failed to generate preview",
          variant: "destructive",
        });
        return;
      }

      setInstancePreview({
        isLoading: false,
        blob: result,
        showOptions: true,
      });

      toast({
        title: "Preview Generated",
        description: "PDF preview is ready",
      });
    } catch (error) {
      console.error("Failed to generate preview", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to generate preview";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setInstancePreview(prev => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  const handleInstancePreviewOpenInNewTab = () => {
    if (!instancePreview.blob) return;

    const url = URL.createObjectURL(instancePreview.blob);
    window.open(url, '_blank');

    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  const handleInstancePreviewDownload = () => {
    if (!instancePreview.blob) return;

    const url = URL.createObjectURL(instancePreview.blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'cfr-preview.pdf';
    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  const handleCancel = () => {
    setFormData({
      template_id: "",
      customer_id: "",
      is_test_send: false,
      contract_number: "",
      contract_type: "",
      contract_quantity: "",
      contract_shipping_dates: "",
      contract_date: "",
    });
    setInstancePreview({
      isLoading: false,
      blob: null,
      showOptions: false,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] md:min-w-[90vw] lg:min-w-[90vw] ">
        <DialogHeader>
          <DialogTitle>Create CFR Instance</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4 md:grid-cols-3">
            <div className="grid gap-2">
              <Label htmlFor="template_id">Template *</Label>
              <Combobox
                className="w-full"
                popoverClassName="min-w-[500px]"
                options={templateOptions}
                value={formData.template_id}
                onValueChange={(value) => setFormData(prev => ({ ...prev, template_id: value }))}
                placeholder="Select template..."
                dwimOnChange
                isModal
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="customer_id">Customer *</Label>
              <Combobox
                className="w-full"
                popoverClassName="min-w-[450px]"
                options={userOptions}
                value={formData.customer_id}
                onValueChange={(value) => setFormData(prev => ({ ...prev, customer_id: value }))}
                placeholder="Select customer..."
                virtualizeList
                dwimOnChange
                isModal
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_test_send"
                checked={formData.is_test_send}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_test_send: !!checked }))}
              />
              <Label htmlFor="is_test_send">Is Test send</Label>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="document_title">Document Title</Label>
              <Input
                id="document_title"
                value={formData.document_title || ""}
                onChange={(e) => setFormData(prev => ({ ...prev, document_title: e.target.value }))}
                placeholder="Enter document title..."
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="contract_number">Contract Number</Label>
              <Input
                id="contract_number"
                value={formData.contract_number || ""}
                onChange={(e) => setFormData(prev => ({ ...prev, contract_number: e.target.value }))}
                placeholder="Enter contract number..."
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="contract_type">Contract Type</Label>
              <Input
                id="contract_type"
                value={formData.contract_type || ""}
                onChange={(e) => setFormData(prev => ({ ...prev, contract_type: e.target.value }))}
                placeholder="Enter contract type..."
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="contract_quantity">Contract Quantity</Label>
              <Input
                id="contract_quantity"
                value={formData.contract_quantity || ""}
                onChange={(e) => setFormData(prev => ({ ...prev, contract_quantity: e.target.value }))}
                placeholder="Enter contract quantity..."
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="contract_shipping_dates">Contract Shipping Dates</Label>
              <Input
                id="contract_shipping_dates"
                value={formData.contract_shipping_dates || ""}
                onChange={(e) => setFormData(prev => ({ ...prev, contract_shipping_dates: e.target.value }))}
                placeholder="Enter shipping dates..."
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="contract_date">Contract Date</Label>
              <Input
                id="contract_date"
                type="date"
                value={formData.contract_date || ""}
                onChange={(e) => setFormData(prev => ({ ...prev, contract_date: e.target.value }))}
              />
            </div>
          </div>

          <div className="flex gap-2 py-2">
            <Button type="button" variant="secondary" onClick={handlePreview} disabled={instancePreview.isLoading}>
              {instancePreview.isLoading ? "Generating Preview..." : "Generate Preview"}
            </Button>
            {instancePreview.showOptions && (
              <>
                <Button type="button" variant="outline" onClick={handleInstancePreviewOpenInNewTab}>
                  Open Preview in New Tab
                </Button>
                <Button type="button" variant="outline" onClick={handleInstancePreviewDownload}>
                  Download Preview
                </Button>
              </>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Creating..." : "Create Instance"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
